import { NgModule } from '@angular/core';
import { Directive, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { CTRL, SHIFT, ALT, TAB } from "nd-common";

@Directive({
	selector: '[clickOutside]'
})
export class ClickOutsideDirective {
	constructor() { }

	private elementIsActive: boolean = false;
	//	These are the elements, or the string IDs of elements, that are checked to see if they contain the clicked element.
	@Input() public containerElements: any[] = [];
	// Click outside directive is not working when we are setting focus manually.
	@Input() public manuallyActiveElement: boolean = false;
	//	Tell the parent to run its close handler because the user clicked outside of the element(s).
	@Output() public clickOutside = new EventEmitter();
	// Tell that we already used manually active element.
	@Output() public manuallyActiveElementUsed = new EventEmitter();

	/**
	 * Mouse click event whose target will either be inside or outside of the containing element(s).
	 * @param $event mouse click event
	 */
	@HostListener('document:contextmenu', ['$event'])
	@HostListener('document:click', ['$event'])
	public onClick( $event: MouseEvent ): void
	{
		if (this.manuallyActiveElement && !this.elementIsActive) {
			this.elementIsActive = true;
			this.manuallyActiveElementUsed.emit();
		}

		if (this.elementIsActive) {
			this.handleEvent($event);
		}
	}

	@HostListener( "click" )
	public elementIsClicked()
	{
		this.elementIsActive = true;
	}
	@HostListener( "focus" )
	public elementIsFocus()
	{
		this.elementIsActive = true;
	}

	@HostListener("change")
	public elementIsChanged() {
		this.elementIsActive = true;
	}

	/**
	 * Keyboard event to handle closing.
	 * @param $event keyboard event to be handled.
	 */
	@HostListener('window:keydown', ['$event'])
	public keyboardInput($event: KeyboardEvent): void {
		if ($event.ctrlKey && $event.keyCode != CTRL
			&& (($event.shiftKey && $event.keyCode != SHIFT) || ($event.altKey && $event.keyCode != ALT))) {
			this.elementIsActive = true;
		}

		if ($event.keyCode === TAB) {
			this.handleEvent($event);
		}
	}

	/**
	 * Handle events and check if we should close element.
	 * @param inputEvent Can be MouseClick or keyboard event etc.
	 */
	private handleEvent(inputEvent: Event): void {
		//	Proceed only if one or more container elements have been specified.
		if (!this.containerElements)
			return;

		//	Get the HTML element that was clicked.
		let clickedElement: Element = inputEvent.srcElement;
		if (clickedElement == null && inputEvent.target) {
			clickedElement = <Element>inputEvent.target;
		}

		//	See whether the clicked element is inside any of the container elements.
		let clickedInside: HTMLElement = this.checkIfClickedInside(clickedElement);

		//	Notify the caller if the user clicked "outside".
		if ( !clickedInside )
		{
			this.elementIsActive = false;
			this.clickOutside.emit(inputEvent);
		}
	}

	/**
	 * See whether the specified element is visible.
	 * @param el HTML element being checked
	 */
	private isVisible(el: HTMLElement): boolean {
		return el.offsetHeight !== null;
	}

	/**
	 * See whether the clicked element is in any of the specified container elements.
	 * @param clickedElement HTML element that was clicked
	 */
	private checkIfClickedInside(clickedElement: Element): HTMLElement {
		return this.containerElements.find(

			e => {
				if (!e) {
					return false;
				}

				let containerElement: Element = null;

				//regular element
				if (e.contains && this.isVisible(e)) {
					containerElement = e;
				}

				//native element
				else if (e.el && this.isVisible(e.el.nativeElement)) {
					containerElement = e.el.nativeElement;
				}

				//_eref element
				else if (e._eref && this.isVisible(e._eref.nativeElement)) {
					containerElement = e._eref.nativeElement;
				}

				//string element
				//	In most cases, the containerElements array will contain DOM elements only. Occasionally, however,
				//	a caller may provide--as a string--the ID of a DOM element that is to be considered a "container element".
				//	This might be done for the lookup dialog, for example (because it is legacy code and cannot be passed to this directive 
				//	as other DOM elements are). In this case, we must locate the corresponding DOM element and check for the clickedElement.
				//	Hitting the DOM like this (with every click) might be criticized as hurting performance, but hitting the DOM just once 
				//	(when the page is loaded) will not work because the corresponding DOM element might not have been loaded yet. And 
				//	saving a reference to the DOM element in the array is dangerous because the element could be removed from the DOM. So, 
				//	until all dialogs are migrated to Palatino, getting a fresh reference to the DOM element seems like the best approach.
				else if (typeof e === "string") {
					containerElement = document.getElementById(e);
				}

				if (containerElement) {
					//	See whether the clicked element is in the specified container element.
					return containerElement.contains(clickedElement);
				} else {
					//	No container element.
					return false;
				}
			}
		);
	}

}

@NgModule({
	declarations: [ClickOutsideDirective],
	exports: [ClickOutsideDirective]
})
export class ndClickOutsideModule {
}
